import React, { useState, useEffect } from 'react';
import { useCart } from './CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Siparis() {
  const { cartItems, subtotal, deliveryFee, total, clearCart, deliveryActive, deliveryClosedReason } = useCart();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  
  // Auth state
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    address_mahalle: '',
    address_sokak: '',
    address_bina_no: '',
    address_kat_daire: '',
    address_tarifi: '',
    payment_method: 'cash',
    note: ''
  });

  // Login form
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  // Register form
  const [registerForm, setRegisterForm] = useState({
    name: '', email: '', phone: '', password: '', password2: ''
  });
  const [authError, setAuthError] = useState('');

  // Kullanıcı kontrolü
  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (token) {
      axios.get(`${API}/auth/me?token=${token}`)
        .then(res => {
          setUser(res.data);
          // Formu doldur
          setForm(prev => ({
            ...prev,
            customer_name: res.data.name || '',
            customer_phone: res.data.phone || '',
            customer_email: res.data.email || '',
            address_mahalle: res.data.address_mahalle || '',
            address_sokak: res.data.address_sokak || '',
            address_bina_no: res.data.address_bina_no || '',
            address_kat_daire: res.data.address_kat_daire || '',
            address_tarifi: res.data.address_tarifi || ''
          }));
        })
        .catch(() => {
          localStorage.removeItem('userToken');
        });
    }
  }, []);

  // Giriş yap
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem('userToken', res.data.token);
      setUser(res.data.user);
      setShowLogin(false);
      // Formu doldur
      setForm(prev => ({
        ...prev,
        customer_name: res.data.user.name || '',
        customer_phone: res.data.user.phone || '',
        customer_email: res.data.user.email || '',
        address_mahalle: res.data.user.address_mahalle || '',
        address_sokak: res.data.user.address_sokak || '',
        address_bina_no: res.data.user.address_bina_no || '',
        address_kat_daire: res.data.user.address_kat_daire || '',
        address_tarifi: res.data.user.address_tarifi || ''
      }));
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Giriş başarısız');
    }
  };

  // Kayıt ol
  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    if (registerForm.password !== registerForm.password2) {
      setAuthError('Şifreler eşleşmiyor');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: registerForm.name,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password
      });
      localStorage.setItem('userToken', res.data.token);
      setUser(res.data.user);
      setShowRegister(false);
      // Formu doldur
      setForm(prev => ({
        ...prev,
        customer_name: res.data.user.name || '',
        customer_phone: res.data.user.phone || '',
        customer_email: res.data.user.email || ''
      }));
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Kayıt başarısız');
    }
  };

  // Çıkış yap
  const handleLogout = () => {
    localStorage.removeItem('userToken');
    setUser(null);
  };

  // Sipariş ver
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        address_mahalle: form.address_mahalle,
        address_sokak: form.address_sokak,
        address_bina_no: form.address_bina_no,
        address_kat_daire: form.address_kat_daire,
        address_tarifi: form.address_tarifi,
        items: cartItems.map(item => {
          const itemPrice = item.calculatedPrice || item.price;
          const itemName = item.sales_unit === 'kg' && item.selectedGram 
            ? `${item.name} – ${item.selectedGram === 1000 ? '1 kg' : item.selectedGram + ' gr'}`
            : item.name;
          return {
            product_id: item.id,
            product_name: itemName,
            quantity: item.quantity,
            unit_price: itemPrice,
            total_price: itemPrice * item.quantity,
            selected_gram: item.selectedGram || null
          };
        }),
        subtotal: subtotal,
        delivery_fee: deliveryFee,
        total: total,
        payment_method: form.payment_method,
        user_id: user?.id || null,
        note: form.note
      };

      const res = await axios.post(`${API}/orders`, orderData);
      
      // Kullanıcı giriş yapmışsa, adres bilgilerini profilde güncelle
      const token = localStorage.getItem('userToken');
      if (token && user) {
        try {
          await axios.put(`${API}/auth/profile?token=${token}`, {
            name: form.customer_name,
            phone: form.customer_phone,
            address_mahalle: form.address_mahalle,
            address_sokak: form.address_sokak,
            address_bina_no: form.address_bina_no,
            address_kat_daire: form.address_kat_daire,
            address_tarifi: form.address_tarifi
          });
        } catch (profileErr) {
          console.error('Profil güncellenemedi:', profileErr);
        }
      }
      
      setOrderNumber(res.data.order_number);
      setOrderComplete(true);
      clearCart();
    } catch (err) {
      alert('Sipariş oluşturulamadı: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Sipariş tamamlandı ekranı
  if (orderComplete) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '20px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '50px' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '80px', marginBottom: '20px' }}>✅</div>
            <h1 style={{ color: '#22c55e', marginBottom: '10px' }}>Siparişiniz Alındı!</h1>
            <p style={{ color: '#6b7280', marginBottom: '30px' }}>
              Siparişiniz en kısa sürede hazırlanacaktır.
            </p>
            
            <div style={{
              backgroundColor: '#f0fdf4',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '30px'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#166534' }}>Sipariş Numaranız:</p>
              <p style={{ 
                margin: 0, 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: '#15803d',
                letterSpacing: '2px'
              }}>
                {orderNumber}
              </p>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
              Siparişinizle ilgili sorularınız için bu numarayı saklayın.
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <a
                href="/"
                style={{
                  padding: '12px 30px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                Ana Sayfaya Dön
              </a>
              <a
                href="/menu"
                style={{
                  padding: '12px 30px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                Menüye Git
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Sepet boşsa
  if (cartItems.length === 0) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '20px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '50px', textAlign: 'center' }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🛒</div>
          <h2>Sepetiniz Boş</h2>
          <p style={{ color: '#6b7280', marginBottom: '30px' }}>
            Sipariş vermek için önce ürün eklemeniz gerekiyor.
          </p>
          <a
            href="/menu"
            style={{
              padding: '15px 40px',
              backgroundColor: '#d97706',
              color: 'white',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: 'bold',
              display: 'inline-block'
            }}
          >
            Menüye Git
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#d97706', color: 'white', padding: '15px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
            <h1 style={{ margin: 0, fontSize: '20px' }}>🍞 MİS FIRIN</h1>
          </a>
          <span style={{ fontSize: '16px' }}>📦 Sipariş</span>
        </div>
      </div>

      {/* Paket Servis Kapalı Uyarısı */}
      {!deliveryActive && (
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
          <div style={{
            backgroundColor: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            padding: '30px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '15px' }}>🔴</div>
            <h2 style={{ color: '#991b1b', marginBottom: '10px' }}>Paket Servis Şu Anda Kapalı</h2>
            <p style={{ color: '#991b1b', marginBottom: '20px', fontSize: '16px' }}>
              {deliveryClosedReason || 'Paket servis geçici olarak hizmet verememektedir.'}
            </p>
            <a
              href="/menu"
              style={{
                display: 'inline-block',
                padding: '12px 30px',
                backgroundColor: '#d97706',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              ← Menüye Dön
            </a>
          </div>
        </div>
      )}

      {deliveryActive && (
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
        {/* Mobile: Önce sepet özeti, sonra form */}
        {/* Desktop: Sol form, sağ sepet */}
        
        {/* Mobile Sepet Özeti (Sadece küçük ekranlarda görünür) */}
        <div className="mobile-cart-summary" style={{ marginBottom: '20px' }}>
          <style>{`
            @media (min-width: 769px) {
              .mobile-cart-summary { display: none !important; }
            }
            @media (max-width: 768px) {
              .desktop-cart-summary { display: none !important; }
              .desktop-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
          <div style={{
            backgroundColor: '#fff7ed',
            borderRadius: '12px',
            padding: '16px',
            border: '2px solid #fed7aa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>🛒 Sepetiniz ({cartItems.length} ürün)</h3>
              <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#d97706' }}>{total.toFixed(2)}₺</span>
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {cartItems.map((item, idx) => {
                const itemName = item.sales_unit === 'kg' && item.selectedGram 
                  ? `${item.name} (${item.selectedGram >= 1000 ? '1 kg' : item.selectedGram + 'gr'})`
                  : item.name;
                return (
                  <span key={item.cartItemId || idx}>
                    {item.quantity}x {itemName}{idx < cartItems.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </div>
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #fed7aa', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span>Teslimat: {deliveryFee === 0 ? <span style={{color:'#22c55e'}}>ÜCRETSİZ</span> : `${deliveryFee.toFixed(2)}₺`}</span>
              <span>Ara Toplam: {subtotal.toFixed(2)}₺</span>
            </div>
          </div>
        </div>

        <div className="desktop-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '20px' }}>
          
          {/* Sol - Form */}
          <div>
            {/* Üyelik Bölümü */}
            {!user && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>👤 Üyelik</h3>
                <p style={{ color: '#6b7280', marginBottom: '15px', fontSize: '14px' }}>
                  Üye olarak sipariş verirseniz, sonraki siparişlerinizde bilgileriniz otomatik doldurulur ve geçmiş siparişlerinizi görebilirsiniz.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => { setShowLogin(true); setShowRegister(false); }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#d97706',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Giriş Yap
                  </button>
                  <button
                    onClick={() => { setShowRegister(true); setShowLogin(false); }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: 'white',
                      color: '#d97706',
                      border: '2px solid #d97706',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Üye Ol
                  </button>
                </div>
                <p style={{ color: '#6b7280', marginTop: '15px', fontSize: '13px' }}>
                  veya üye olmadan devam edin ↓
                </p>
              </div>
            )}

            {/* Üye bilgisi */}
            {user && (
              <div style={{
                backgroundColor: '#f0fdf4',
                borderRadius: '12px',
                padding: '15px 20px',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: '600', color: '#166534' }}>
                    👤 {user.name}
                  </p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#15803d' }}>
                    {user.email}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Çıkış
                </button>
              </div>
            )}

            {/* Sipariş Formu */}
            <form onSubmit={handleSubmit}>
              {/* Kişisel Bilgiler */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0 }}>📞 İletişim Bilgileri</h3>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Ad Soyad *</label>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={(e) => setForm({...form, customer_name: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Telefon *</label>
                  <input
                    type="tel"
                    value={form.customer_phone}
                    onChange={(e) => setForm({...form, customer_phone: e.target.value})}
                    required
                    placeholder="05XX XXX XX XX"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>E-posta (opsiyonel)</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm({...form, customer_email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Adres */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0 }}>📍 Teslimat Adresi</h3>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Mahalle *</label>
                  <input
                    type="text"
                    value={form.address_mahalle}
                    onChange={(e) => setForm({...form, address_mahalle: e.target.value})}
                    required
                    placeholder="Örn: Cevizli Mahallesi"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Sokak/Cadde *</label>
                  <input
                    type="text"
                    value={form.address_sokak}
                    onChange={(e) => setForm({...form, address_sokak: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Bina No *</label>
                    <input
                      type="text"
                      value={form.address_bina_no}
                      onChange={(e) => setForm({...form, address_bina_no: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Kat/Daire</label>
                    <input
                      type="text"
                      value={form.address_kat_daire}
                      onChange={(e) => setForm({...form, address_kat_daire: e.target.value})}
                      placeholder="Örn: 3/5"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Adres Tarifi</label>
                  <textarea
                    value={form.address_tarifi}
                    onChange={(e) => setForm({...form, address_tarifi: e.target.value})}
                    placeholder="Örn: Kırmızı binanın yanı, marketin karşısı..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      minHeight: '80px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Ödeme Yöntemi */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0 }}>💳 Ödeme Yöntemi</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '15px',
                    border: form.payment_method === 'cash' ? '2px solid #d97706' : '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: form.payment_method === 'cash' ? '#fff7ed' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="payment"
                      value="cash"
                      checked={form.payment_method === 'cash'}
                      onChange={(e) => setForm({...form, payment_method: e.target.value})}
                      style={{ marginRight: '12px', width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '20px', marginRight: '10px' }}>💵</span>
                    <span style={{ fontWeight: '600' }}>Kapıda Nakit Ödeme</span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '15px',
                    border: form.payment_method === 'card_on_delivery' ? '2px solid #d97706' : '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: form.payment_method === 'card_on_delivery' ? '#fff7ed' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="payment"
                      value="card_on_delivery"
                      checked={form.payment_method === 'card_on_delivery'}
                      onChange={(e) => setForm({...form, payment_method: e.target.value})}
                      style={{ marginRight: '12px', width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '20px', marginRight: '10px' }}>💳</span>
                    <span style={{ fontWeight: '600' }}>Kapıda Kredi Kartı</span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '15px',
                    border: form.payment_method === 'online_iyzico' ? '2px solid #d97706' : '2px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: form.payment_method === 'online_iyzico' ? '#fff7ed' : '#f9fafb',
                    opacity: 0.6
                  }}>
                    <input
                      type="radio"
                      name="payment"
                      value="online_iyzico"
                      checked={form.payment_method === 'online_iyzico'}
                      onChange={(e) => setForm({...form, payment_method: e.target.value})}
                      disabled
                      style={{ marginRight: '12px', width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '20px', marginRight: '10px' }}>🌐</span>
                    <div>
                      <span style={{ fontWeight: '600' }}>Online Ödeme (iyzico)</span>
                      <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Yakında aktif olacak</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Sipariş Notu */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0 }}>📝 Sipariş Notu</h3>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({...form, note: e.target.value})}
                  placeholder="Eklemek istediğiniz notlar (opsiyonel)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '18px',
                  backgroundColor: loading ? '#9ca3af' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Sipariş Gönderiliyor...' : `Siparişi Onayla (${total.toFixed(2)}₺)`}
              </button>
            </form>
          </div>

          {/* Sağ - Sepet Özeti (Desktop) */}
          <div className="desktop-cart-summary">
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'sticky',
              top: '20px'
            }}>
              <h3 style={{ marginTop: 0 }}>🛒 Sepet Özeti</h3>
              
              <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '15px' }}>
                {cartItems.map(item => {
                  const itemPrice = item.calculatedPrice || item.price;
                  const itemName = item.sales_unit === 'kg' && item.selectedGram 
                    ? `${item.name} – ${item.selectedGram === 1000 ? '1 kg' : item.selectedGram + ' gr'}`
                    : item.name;
                  return (
                    <div 
                      key={item.cartItemId || item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>{itemName}</p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                          {item.quantity} x {itemPrice.toFixed(2)}₺
                        </p>
                      </div>
                      <span style={{ fontWeight: '600' }}>
                        {(itemPrice * item.quantity).toFixed(2)}₺
                      </span>
                    </div>
                  );
                })}
              </div>

              <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span>Ara Toplam:</span>
                  <span>{subtotal.toFixed(2)}₺</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span>Teslimat:</span>
                  <span style={{ color: deliveryFee === 0 ? '#22c55e' : '#111' }}>
                    {deliveryFee === 0 ? 'ÜCRETSİZ' : `${deliveryFee.toFixed(2)}₺`}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  paddingTop: '10px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <span>Toplam:</span>
                  <span style={{ color: '#d97706' }}>{total.toFixed(2)}₺</span>
                </div>
              </div>

              <a
                href="/menu"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '15px',
                  color: '#d97706',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                ← Alışverişe Devam Et
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h2 style={{ marginTop: 0 }}>Giriş Yap</h2>
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>E-posta</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Şifre</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              {authError && (
                <p style={{ color: '#dc2626', marginBottom: '15px' }}>{authError}</p>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#d97706',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Giriş
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Üye Ol</h2>
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Ad Soyad</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>E-posta</label>
                <input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Telefon</label>
                <input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Şifre</label>
                <input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Şifre Tekrar</label>
                <input
                  type="password"
                  value={registerForm.password2}
                  onChange={(e) => setRegisterForm({...registerForm, password2: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              {authError && (
                <p style={{ color: '#dc2626', marginBottom: '15px' }}>{authError}</p>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#d97706',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Kayıt Ol
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Siparis;
